import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import AdSlot from "../components/AdSlot";
import { toast } from "sonner";

const CODES = ["all", "NFPA 13", "NFPA 14", "NFPA 20"];

function ArticleForm({ initial, onDone }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial || { code: "NFPA 13", section: "", title: "", content: "", tags: [] });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (initial?.id) return (await api.put(`/nfpa/${initial.id}`, payload)).data;
      return (await api.post("/nfpa", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["nfpa"] }); toast.success("Saved"); onDone(); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-tech">Code</Label>
          <select value={form.code} onChange={upd("code")} className="w-full h-10 border border-input px-3 mt-1 rounded-none bg-white" data-testid="article-code-select">
            {CODES.slice(1).map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-tech">Section</Label>
          <Input value={form.section} onChange={upd("section")} className="rounded-none mt-1" data-testid="article-section-input" />
        </div>
      </div>
      <div>
        <Label className="label-tech">Title</Label>
        <Input value={form.title} onChange={upd("title")} className="rounded-none mt-1" data-testid="article-title-input" />
      </div>
      <div>
        <Label className="label-tech">Content</Label>
        <Textarea value={form.content} onChange={upd("content")} rows={8} className="rounded-none mt-1 font-mono text-sm" data-testid="article-content-input" />
      </div>
      <div>
        <Label className="label-tech">Tags (comma separated)</Label>
        <Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={upd("tags")} className="rounded-none mt-1" data-testid="article-tags-input" />
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="article-save-btn">
        {save.isPending ? "Saving…" : "Save Article"}
      </Button>
    </div>
  );
}

export default function NFPAPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [code, setCode] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: articles = [] } = useQuery({
    queryKey: ["nfpa", code, q],
    queryFn: async () => (await api.get(`/nfpa?code=${code}&q=${encodeURIComponent(q)}`)).data,
  });

  const active = selected || articles[0];
  const isAdmin = user && user.role === "admin";

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/nfpa/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["nfpa"] }); setSelected(null); toast.success("Deleted"); },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="label-tech text-primary">Knowledge Base</span>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black mt-2">NFPA Code Reference</h1>
          <p className="text-muted-foreground mt-2">Searchable reference on NFPA 13, 14 &amp; 20.</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-primary hover:bg-[#D62828] font-bold" data-testid="add-article-btn"><Plus className="w-4 h-4 mr-1" /> New Article</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-2 border-foreground max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
              <ArticleForm initial={editing} onDone={() => { setDialogOpen(false); setEditing(null); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search codes, sections, keywords…" className="rounded-none pl-10 h-12" data-testid="nfpa-search" />
      </div>

      <div className="flex gap-2 mb-8 flex-wrap">
        {CODES.map((c) => (
          <button key={c} onClick={() => setCode(c)} data-testid={`code-filter-${c.replace(" ", "-")}`}
            className={`px-4 py-2 text-sm font-bold border transition-colors ${code === c ? "bg-primary text-white border-primary" : "border-foreground hover:bg-foreground hover:text-white"}`}>
            {c === "all" ? "All Codes" : c}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* sidebar list */}
        <aside className="lg:col-span-4 border border-border bg-white max-h-[70vh] overflow-y-auto">
          {articles.map((a) => (
            <button key={a.id} onClick={() => setSelected(a)} data-testid={`article-item-${a.id}`}
              className={`w-full text-left p-4 border-b border-border transition-colors ${active?.id === a.id ? "bg-[#0A0A0A] text-white" : "hover:bg-secondary"}`}>
              <span className={`font-mono text-xs ${active?.id === a.id ? "text-primary" : "text-primary"}`}>{a.code} · {a.section}</span>
              <p className="font-semibold text-sm mt-1">{a.title}</p>
            </button>
          ))}
          {articles.length === 0 && <p className="p-4 text-sm text-muted-foreground">No articles found.</p>}
        </aside>

        {/* content */}
        <div className="lg:col-span-8 space-y-8">
          {active ? (
            <article className="border border-border bg-white p-8" data-testid="article-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="font-mono text-sm text-primary">{active.code} — Section {active.section}</span>
                  <h2 className="font-display text-3xl font-bold mt-1">{active.title}</h2>
                </div>
                {isAdmin && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="icon" variant="outline" className="rounded-none" onClick={() => { setEditing({ ...active }); setDialogOpen(true); }} data-testid={`edit-article-${active.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="outline" className="rounded-none text-primary" onClick={() => del.mutate(active.id)} data-testid={`delete-article-${active.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              <div className="mt-6 font-mono text-sm leading-relaxed bg-secondary border border-border p-6 whitespace-pre-wrap">{active.content}</div>
              {active.tags?.length > 0 && (
                <div className="flex gap-2 mt-6 flex-wrap">
                  {active.tags.map((t) => <span key={t} className="label-tech border border-border px-2 py-1">{t}</span>)}
                </div>
              )}
            </article>
          ) : (
            <div className="border border-border bg-white p-12 text-center text-muted-foreground">
              <BookOpen className="w-10 h-10 mx-auto mb-3" /> Select an article to read.
            </div>
          )}
          <AdSlot placement="sidebar" />
        </div>
      </div>
    </div>
  );
}
